import dayjs from "dayjs"

import { CompanyCertificationTypeLabelMap } from "./certificationDataMap"

import { DATE_FORMAT } from "@/constants/formats"
import type { CompanyCertification, CompanyCertificationType, Location } from "@/graphql/codegen/graphql"
import type { CompanyCertificationAddFormValues } from "@/screens/Companies/edit/components/certification/schema/CompanyCertificationSchema"
import type {
  GroupedByCertificationType,
  GroupedByLocation,
  TransformedCertificationTableItem,
} from "@/screens/Companies/edit/components/certification/types"
import { CertificationStatus } from "@/screens/Companies/edit/components/certification/types"

export const getCertificationStatus = (expirationDate: string): CertificationStatus => {
  const diffInDays = dayjs(expirationDate).diff(dayjs(), "day")

  if (diffInDays <= 0) return CertificationStatus.EXPIRED
  if (diffInDays <= 90) return CertificationStatus.LESS_THAN

  return CertificationStatus.MORE_THAN
}

const handleSortByExpirationDate = (a: CompanyCertification, b: CompanyCertification) =>
  dayjs(b.expirationDate).diff(dayjs(a.expirationDate))

// If there is a gap between the expiration date of the current certification and the effective date of the upcoming certification, the location's color status is determines by current expiry date
export const certificationStatusByGroupedLocation = (location: GroupedByLocation): CertificationStatus => {
  const now = dayjs()
  const certificates = location.certificates

  const currentCertificate = [
    ...certificates.filter((cert) => {
      return dayjs(cert.effectiveDate).isBefore(now)
    }),
  ].sort(handleSortByExpirationDate)?.[0]

  const upcomingCerts = [
    ...certificates.filter((cert) => {
      return dayjs(cert.effectiveDate).isAfter(now)
    }),
  ].sort(handleSortByExpirationDate)

  if (!currentCertificate || dayjs(currentCertificate.expirationDate) < now) {
    return CertificationStatus.EXPIRED
  }

  let previousEndDate = dayjs(currentCertificate.expirationDate)

  for (const certificate of upcomingCerts) {
    const certificateStart = dayjs(certificate.effectiveDate)

    // Check if there's a gap between previous coverage end and current cert start
    // A gap exists if current cert starts more than 1 day after previous cert expires
    const hasGap = certificateStart.diff(previousEndDate, "day") > 1

    if (!hasGap) {
      // No gap - extend coverage to current cert's expiration
      previousEndDate = dayjs(certificate.expirationDate)
    }
  }

  return getCertificationStatus(previousEndDate.format(DATE_FORMAT.ISO_18601))
}

export const hasExpiredCertificates = (certs: CompanyCertificationAddFormValues[]): boolean => {
  const today = dayjs().startOf("day")

  return certs.some((cert) => {
    const expiration = dayjs(cert.expirationDate)
    return expiration.isBefore(today, "day")
  })
}

export const groupCertificationsByLocation = (certifications: CompanyCertification[]): GroupedByLocation[] => {
  const map = new Map<string, GroupedByLocation>()

  certifications.forEach((cert) => {
    const location = cert.location
    const locationId = location?.locationId

    if (!location || !locationId) return

    if (!map.has(locationId)) {
      map.set(locationId, {
        location,
        certificates: [],
      })
    }

    map.get(locationId)!.certificates.push(cert)
  })

  return Array.from(map.values())
}

export const groupByTypeAndLocation = (certificates: CompanyCertification[]): GroupedByCertificationType[] => {
  const typeMap = new Map<CompanyCertificationType, Map<string, GroupedByLocation>>()

  for (const cert of certificates) {
    const certificationType = cert.certificationType as CompanyCertificationType
    const locationId = cert?.locationId as string

    if (!typeMap.has(certificationType)) {
      typeMap.set(certificationType, new Map())
    }

    const locationMap = typeMap.get(certificationType)!

    if (!locationMap.has(locationId)) {
      locationMap.set(locationId, {
        location: cert.location!,
        certificates: [],
      })
    }
    locationMap.get(locationId)!.certificates.push(cert)
  }

  return Array.from(typeMap.entries()).map(([certificationType, locationMap]) => ({
    certificationType,
    locations: Array.from(locationMap.values()),
  }))
}

export const transformCertificationTableData = (
  group: GroupedByCertificationType,
  locations: Location[],
  showAllLocations: boolean
): TransformedCertificationTableItem[] => {
  const now = dayjs()
  const result = []

  for (const location of locations) {
    const groupedLocation = group.locations.find((gl) => gl.location.locationId === location.locationId)

    if (!groupedLocation && !showAllLocations) {
      continue
    }

    const item: TransformedCertificationTableItem = { location }

    if (groupedLocation) {
      const currentCerts = groupedLocation?.certificates.filter((cert) => {
        return dayjs(cert.effectiveDate).isBefore(now)
      })

      const upcomingCerts = groupedLocation?.certificates.filter((cert) => {
        return dayjs(cert.effectiveDate).isAfter(now)
      })

      const currentCert = [...currentCerts].sort(handleSortByExpirationDate)[0]
      const upcomingCert = [...upcomingCerts].sort(handleSortByExpirationDate)[0]

      if (currentCert) {
        item.current = currentCert
      }

      if (upcomingCert) {
        item.upcoming = upcomingCert
      }
    }

    result.push(item)
  }

  return result.sort((a, b) => {
    const aHasCert = a.current || a.upcoming ? 1 : 0
    const bHasCert = b.current || b.upcoming ? 1 : 0
    return bHasCert - aHasCert
  })
}

export const extractCertificationIds = (data: TransformedCertificationTableItem[]): string[] => {
  return data.flatMap(({ current, upcoming }) => {
    const ids: string[] = []
    if (current?.companyCertificationId) {
      ids.push(current.companyCertificationId)
    }
    if (upcoming?.companyCertificationId) {
      ids.push(upcoming.companyCertificationId)
    }
    return ids
  })
}

export const extractHistoryCertificationIds = (data: GroupedByLocation[]): string[] => {
  return data.flatMap((group) => group.certificates.map((cert) => cert.companyCertificationId!))
}

export const getCertificationLabel = (certificationType: CompanyCertificationType | null): string => {
  return certificationType ? (CompanyCertificationTypeLabelMap.get(certificationType) ?? certificationType) : "-"
}

export const extractSelectedCertificationIds = (
  groupedCertification: GroupedByCertificationType,
  selectedKeys: string[]
): string[] => {
  return groupedCertification.locations
    .filter((groupedByLocation) => selectedKeys.includes(groupedByLocation.location.locationId.toString()))
    .filter(Boolean)
    .flatMap((group) => group.certificates.map((cert) => cert.companyCertificationId as string))
}
